import React, { useState, useEffect, useRef, useContext } from "react";
import "./ReportPage.css";
import Select from "react-select";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { TbRefresh } from "react-icons/tb";
import Tooltip from "@mui/material/Tooltip";
import API_URL from "../Config";
import { TextField } from "@mui/material";
import { MdArrowForwardIos } from "react-icons/md";
import DatePicker from "react-datepicker";
import ExportPopup from "../Popups/ExportPopup";
import { ThemeContext } from "../ThemeContext";

function SchedulesReport() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [isExportPopupOpen, setIsExportPopupOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [reportStartDate, setReportStartDate] = useState(null);
  const [reportEndDate, setReportEndDate] = useState(null);
  const navigate = useNavigate();

  const { theme } = useContext(ThemeContext);

  // ------------------------------------------------------------
  // EXPORT
  // ------------------------------------------------------------
  const handleExportButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
    setIsExportPopupOpen(true);
  };

  const handleExport = async (format, filter, startDate, endDate) => {
    try {
      const authKey = localStorage.getItem("authKey");

      const params = new URLSearchParams({
        format,
        filter_option: filter.value,
        ...(filter.value === "custom_date" && startDate && endDate && {
          start_date: formatDate(startDate),
          end_date: formatDate(endDate),
        }),
      }).toString();

      const response = await axios.get(
        `${API_URL}/api/export_procurement_report?${params}`,
        {
          headers: { Authorization: authKey },
          responseType: "blob",
        }
      );

      if (response.status === 200) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `procurement_report.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error("Error exporting report:", error.message);
    }
  };

  // ------------------------------------------------------------
  // FETCH DATA
  // ------------------------------------------------------------
  const fetchData = async (params) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.get(`${API_URL}/api/pr_report`, {
        headers: { Authorization: authKey },
        params,
      });

      if (response.status === 200 && response.data) {
        const dateRange = response.data.date_range;

        if (dateRange.start_date && dateRange.end_date) {
          setReportStartDate(dateRange.start_date);
          setReportEndDate(dateRange.end_date);
        } else if (dateRange.date) {
          setReportStartDate(dateRange.date);
          setReportEndDate(null);
        }

        setReports(response.data.report || []);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching report:", error.message);
      setLoading(false);
    }
  };

  // ------------------------------------------------------------
  // FILTER OPTIONS
  // ------------------------------------------------------------
  const filterOptions = [
    { label: "All", value: "all" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Tomorrow", value: "tomorrow" },
    { label: "Next 7 Days", value: "next7days" },
    { label: "Next Month", value: "next_month" },
    { label: "Current Week", value: "current_week" },
    { label: "Previous Month", value: "previous_month" },
    { label: "Previous Week", value: "past_week" },
    { label: "Current Month", value: "current_month" },
    { label: "Custom Date", value: "custom_date" },
  ];

  const [selectedFilter, setSelectedFilter] = useState(
    filterOptions.find((opt) => opt.value === "current_month")
  );

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString("en-CA").replace(/-/g, "/");
  };

  const handleFilterScheduler = async (filter_option, start_date, end_date) => {
    let params = {};

    if (filter_option === "custom_date" && start_date && end_date) {
      params.start_date = formatDate(start_date);
      params.end_date = formatDate(end_date);
    } else {
      params.filter_option = filter_option;
    }

    fetchData(params);
  };

  const handleFilterChange = (selectedOption) => {
    setSelectedFilter(selectedOption);
  };

  useEffect(() => {
    if (selectedFilter.value !== "custom_date") {
      handleFilterScheduler(selectedFilter.value);
    } else if (selectedStartDate && selectedEndDate) {
      handleFilterScheduler(
        selectedFilter.value,
        selectedStartDate,
        selectedEndDate
      );
    }
  }, [selectedFilter, selectedStartDate, selectedEndDate]);

  // ------------------------------------------------------------
  // REFRESH
  // ------------------------------------------------------------
  const handleRefresh = () => {
    if (selectedFilter.value !== "custom_date") {
      handleFilterScheduler(selectedFilter.value);
    } else if (selectedStartDate && selectedEndDate) {
      handleFilterScheduler(
        selectedFilter.value,
        selectedStartDate,
        selectedEndDate
      );
    }
  };

  // ------------------------------------------------------------
  // CONDITIONAL NAVIGATION (IMPORTANT FIX)
  // ------------------------------------------------------------
  const formatDateToYMD = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const handleViewClick = (department_id, pr_status) => {
    const isCustom = selectedFilter.value === "custom_date";
    const start = isCustom ? selectedStartDate : reportStartDate;
    const end = isCustom ? selectedEndDate : reportEndDate;

    const formattedStart = start ? formatDateToYMD(start) : "";
    const formattedEnd = end ? formatDateToYMD(end) : "";

    let path = `/procurement-report-view/${department_id}/${selectedFilter.value}/${pr_status}`;

    if (formattedStart && formattedEnd) {
      path += `/${formattedStart}/${formattedEnd}`;
    }

    navigate(path);
  };

  // ------------------------------------------------------------
  // DATE FORMATTER FOR HEADER
  // ------------------------------------------------------------
  const formatDates = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d)) return "";
    return d.toLocaleDateString("en-GB");
  };

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------
  return (
    <>
      <div className="data-list-content">
        <div className="data-list-header">
          <div className="data-list-header-content">
            <div className="data-list-heading-content-left-side">
              Procurement Reports
              {selectedFilter.value !== "custom_date" ? (
                reportStartDate && reportEndDate ? (
                  <span style={{ color: "#007bff" }}>
                    (Date Range: {formatDates(reportStartDate)} -{" "}
                    {formatDates(reportEndDate)})
                  </span>
                ) : reportStartDate ? (
                  <span style={{ color: "#007bff" }}>
                    (Date: {formatDates(reportStartDate)})
                  </span>
                ) : null
              ) : selectedStartDate && selectedEndDate ? (
                <span style={{ color: "#007bff" }}>
                  (Date Range: {formatDates(selectedStartDate)} -{" "}
                  {formatDates(selectedEndDate)})
                </span>
              ) : null}
            </div>

            {/* FILTER + EXPORT + REFRESH */}
            <div className="data-list-heading-content-right-side">
              <div className="dropdown-container">
                <Select
                  value={selectedFilter}
                  onChange={handleFilterChange}
                  options={filterOptions}
                  className="project-dropdown"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      backgroundColor: theme === "dark" ? "black" : "white",
                      color: theme === "dark" ? "#fff" : "#333",
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 999,
                      backgroundColor: theme === "dark" ? "#000" : "#fff",
                    }),
                  }}
                />
              </div>

              {selectedFilter.value === "custom_date" && (
                <div className="input-group-datepickers">
                  <DatePicker
                    placeholderText="Select Date Range"
                    className="dates"
                    startDate={selectedStartDate}
                    endDate={selectedEndDate}
                    selectsRange={true}
                    dateFormat="dd/MM/yyyy"
                    isClearable
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setSelectedStartDate(start);
                      setSelectedEndDate(end);
                    }}
                  />
                </div>
              )}

              <button className="export-btn" onClick={handleExportButtonClick}>
                Export
              </button>

              <ExportPopup
                isOpen={isExportPopupOpen}
                onClose={() => {
                  setIsExportPopupOpen(false);
                  setAnchorEl(null);
                }}
                onExport={handleExport}
                selectedFilter={selectedFilter}
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
                anchorEl={anchorEl}
              />

              <Tooltip title="Refresh" arrow>
                <button className="refresh-button" onClick={handleRefresh}>
                  <TbRefresh className="refresh-icon" />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="data-list-body">
          <div className="data-list-scroll-body-table">
            <div className="data-list-table-report">
              {loading ? (
                <div className="loader-container">
                  <div className="loader"></div>
                  <div>Loading...</div>
                </div>
              ) : reports.length > 0 ? (
                <table className="table multilevel-data">
                  <thead>
                    <tr>
                      <th>SR. NO.</th>
                      <th>DEPARTMENT</th>
                      <th className="center-alignment">APPROVE PENDING</th>
                      <th className="center-alignment">APPROVED</th>
                      <th className="center-alignment">REJECTED</th>
                      <th className="center-alignment">RETURNED</th>
                      <th className="center-alignment">TOTAL PR REQUESTS</th>
                    </tr>
                  </thead>

                  <tbody>
                    {reports.map((report, i) => (
                      <tr key={i} className="hoverable-row">
                        <td>{i + 1}</td>
                        <td>{report.department_name || "NA"}</td>

                        <td
                          className={`approve-status center-alignment ${
                            report.pending === "0" ? "non-clickable" : ""
                          }`}
                          onClick={() =>
                            report.pending > 0 &&
                            handleViewClick(report.department_id, "pending")
                          }
                        >
                          {report.pending || "0"}
                          {report.pending > 0 && (
                            <MdArrowForwardIos className="total-report-icon" />
                          )}
                        </td>

                        <td
                          className={`approved-status center-alignment ${
                            report.approved === "0" ? "non-clickable" : ""
                          }`}
                          onClick={() =>
                            report.approved > 0 &&
                            handleViewClick(report.department_id, "approved")
                          }
                        >
                          {report.approved || "0"}
                          {report.approved > 0 && (
                            <MdArrowForwardIos className="total-report-icon" />
                          )}
                        </td>

                        <td
                          className={`rejected-status center-alignment ${
                            report.rejected === "0" ? "non-clickable" : ""
                          }`}
                          onClick={() =>
                            report.rejected > 0 &&
                            handleViewClick(report.department_id, "rejected")
                          }
                        >
                          {report.rejected || "0"}
                          {report.rejected > 0 && (
                            <MdArrowForwardIos className="total-report-icon" />
                          )}
                        </td>

                        <td
                          className={`returned-status center-alignment ${
                            report.returned === "0" ? "non-clickable" : ""
                          }`}
                          onClick={() =>
                            report.returned > 0 &&
                            handleViewClick(report.department_id, "returned")
                          }
                        >
                          {report.returned || "0"}
                          {report.returned > 0 && (
                            <MdArrowForwardIos className="total-report-icon" />
                          )}
                        </td>

                        <td
                          className="total-status center-alignment"
                          onClick={() =>
                            handleViewClick(report.department_id, "total_schedules")
                          }
                        >
                          {report.total_pr || "0"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-users-container">
                  <div className="no-users-message">
                    No Schedules Report Found
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SchedulesReport;













// import React, { useState, useEffect, useRef, useContext } from "react";
// import "./ReportPage.css";
// import Select from "react-select";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import { TbRefresh } from "react-icons/tb";
// import Tooltip from "@mui/material/Tooltip";
// import API_URL from "../Config";
// import { TextField } from "@mui/material";
// import { MdArrowForwardIos } from "react-icons/md";
// import DatePicker from "react-datepicker";
// import ExportPopup from "../Popups/ExportPopup";
// import { ThemeContext } from "../ThemeContext";

// function SchedulesReport() {
//   const [reports, setReports] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [selectedStartDate, setSelectedStartDate] = useState(null);
//   const [selectedEndDate, setSelectedEndDate] = useState(null);
//   const [isExportPopupOpen, setIsExportPopupOpen] = useState(false);
//   const [anchorEl, setAnchorEl] = useState(null);
//   const [reportStartDate, setReportStartDate] = useState(null);
//   const [reportEndDate, setReportEndDate] = useState(null);
//   const navigate = useNavigate();

//   const handleExportButtonClick = (event) => {
//     setAnchorEl(event.currentTarget);
//     setIsExportPopupOpen(true);
//   };

//   const { theme } = useContext(ThemeContext);

//   const handleExport = async (format, filter, startDate, endDate) => {
//     try {
//       const authKey = localStorage.getItem("authKey");
//       const params = new URLSearchParams({
//         format,
//         filter_option: filter.value,
//         ...(filter.value === "custom_date" && {
//           start_date: formatDate(startDate),
//           end_date: formatDate(endDate),
//         }),
//       }).toString();

//       const response = await axios.get(
//         `${API_URL}/api/export_procurement_report?${params}`,
//         {
//           headers: {
//             Authorization: authKey,
//           },
//           responseType: "blob", // To handle binary file data
//         }
//       );

//       if (response.status === 200) {
//         const url = window.URL.createObjectURL(new Blob([response.data]));
//         const link = document.createElement("a");
//         link.href = url;
//         link.setAttribute("download", `procurement_report.${format}`);
//         document.body.appendChild(link);
//         link.click();
//         link.remove();
//       } else {
//         console.error("Error exporting schedule:", response.statusText);
//       }
//     } catch (error) {
//       console.error("Error exporting schedule:", error.message);
//     }
//   };

//   const fetchData = async (params) => {
//     try {
//       setLoading(true);
//       const authKey = localStorage.getItem("authKey");

//       const response = await axios.get(`${API_URL}/api/pr_report`, {
//         headers: {
//           Authorization: authKey,
//         },
//         params: params,
//       });
//       if (response.status === 200) {
//         console.log("Filtered response:", response.data); // Log the response
//         if (response.data) {
//           const dateRange = response.data.date_range;
//           if (dateRange.start_date && dateRange.end_date) {
//             setReportStartDate(dateRange.start_date);
//             setReportEndDate(dateRange.end_date);
//           } else if (dateRange.date) {
//             setReportStartDate(dateRange.date);
//             setReportEndDate(null); // Clear end date if not available
//           }
//         }
//       }
//       setReports(response.data.report);
//       setLoading(false);
//     } catch (error) {
//       console.error("Error fetching schedule report:", error.message);
//       setLoading(false);
//     }
//   };

//   const filterOptions = [
//     { label: "All", value: "all" },
//     { label: "Today", value: "today" },
//     { label: "Yesterday", value: "yesterday" },
//     { label: "Tomorrow", value: "tomorrow" },
//     { label: "Next 7 Days", value: "next7days" },
//     { label: "Next Month", value: "next_month" },
//     { label: "Current Week", value: "current_week" },
//     { label: "Previous Month", value: "previous_month" },
//     { label: "Previous Week", value: "past_week" },
//     { label: "Current Month", value: "current_month" },
//     { label: "Custom Date", value: "custom_date" },
//   ];
//   const [selectedFilter, setSelectedFilter] = useState(
//     filterOptions.find((option) => option.value === "current_month")
//   );
//   const formatDate = (date) => {
//     const options = { year: "numeric", month: "2-digit", day: "2-digit" };
//     return new Date(date)
//       .toLocaleDateString("en-CA", options)
//       .replace(/-/g, "/");
//   };

//   const handleFilterScheduler = async (
//     filter_option,
//     start_date = null,
//     end_date = null
//   ) => {
//     try {
//       let params = {};

//       if (filter_option === "custom_date" && start_date && end_date) {
//         params.start_date = formatDate(start_date);
//         params.end_date = formatDate(end_date);
//       } else {
//         params.filter_option = filter_option;
//       }

//       // Only this fetch call, removing the duplicate axios.get request
//       await fetchData(params);
//     } catch (error) {
//       console.error("Error fetching scheduler data:", error.message);
//     }
//   };
//   const handleFilterChange = (selectedOption) => {
//     setSelectedFilter(selectedOption);
//   };

//   useEffect(() => {
//     if (selectedFilter && selectedFilter.value !== "custom_date") {
//       handleFilterScheduler(selectedFilter.value);
//     } else if (
//       selectedFilter &&
//       selectedFilter.value === "custom_date" &&
//       selectedStartDate &&
//       selectedEndDate
//     ) {
//       handleFilterScheduler(
//         selectedFilter.value,
//         selectedStartDate,
//         selectedEndDate
//       );
//     }
//   }, [selectedFilter, selectedStartDate, selectedEndDate]);

//   const containerRef = useRef(null);

//   const handleRefresh = () => {
//     if (selectedFilter && selectedFilter.value !== "custom_date") {
//       handleFilterScheduler(selectedFilter.value);
//     } else if (
//       selectedFilter &&
//       selectedFilter.value === "custom_date" &&
//       selectedStartDate &&
//       selectedEndDate
//     ) {
//       handleFilterScheduler(
//         selectedFilter.value,
//         selectedStartDate,
//         selectedEndDate
//       );
//     }
//   };

//   const formatDateToYMD = (date) => {
//     if (!date) return null;
//     const d = new Date(date);
//     const year = d.getFullYear();
//     const month = `${d.getMonth() + 1}`.padStart(2, "0");
//     const day = `${d.getDate()}`.padStart(2, "0");
//     return `${year}-${month}-${day}`;
//   };

//   const handleViewClick = async (department_id, pr_status) => {
//     const isCustomDate = selectedFilter.value === "custom_date";

//     const startDate = isCustomDate ? selectedStartDate : reportStartDate;
//     const endDate = isCustomDate ? selectedEndDate : reportEndDate;

//     const formattedStart = formatDateToYMD(startDate);
//     const formattedEnd = formatDateToYMD(endDate);

//     navigate(
//       `/procurement-report-view/${department_id}/${selectedFilter.value}/${pr_status}/${formattedStart}/${formattedEnd}`
//     );
//   };

//   const formatDates = (dateString) => {
//     if (!dateString || typeof dateString !== "string") {
//       return ""; // Return an empty string if the date is invalid
//     }

//     const date = new Date(dateString);

//     // Check if the date is valid
//     if (isNaN(date.getTime())) {
//       return ""; // Return an empty string for invalid dates
//     }

//     const formatter = new Intl.DateTimeFormat("en-GB", {
//       day: "2-digit",
//       month: "2-digit",
//       year: "2-digit",
//     });
//     return formatter.format(date);
//   };

//   return (
//     <>
//       <div className="data-list-content" ref={containerRef}>
//         <div className="data-list-header">
//           <div className="data-list-header-content">
//             <div className="data-list-heading-content-left-side">
//               Procurement Reports
//               {selectedFilter.value === "all" &&
//               reportStartDate === "All dates" ? (
//                 <span>
//                   <span style={{ color: "#007bff" }}> (Date: All dates)</span>
//                 </span>
//               ) : selectedFilter.value !== "all" &&
//                 reportStartDate &&
//                 reportEndDate ? (
//                 <span>
//                   <span style={{ color: "#007bff" }}>
//                     {" "}
//                     (Date Range: {formatDates(reportStartDate)} -{" "}
//                     {formatDates(reportEndDate)}){" "}
//                   </span>
//                 </span>
//               ) : selectedFilter.value !== "all" && reportStartDate ? (
//                 <span>
//                   <span style={{ color: "#007bff" }}>
//                     {" "}
//                     (Date: {formatDates(reportStartDate)}){" "}
//                   </span>
//                 </span>
//               ) : null}
//             </div>

//             <div className="data-list-heading-content-right-side">
//               <div className="dropdown-container">
//                 <Select
//                   value={selectedFilter}
//                   onChange={handleFilterChange}
//                   options={filterOptions}
//                   className="project-dropdown"
//                   styles={{
//                     control: (provided) => ({
//                       ...provided,
//                       backgroundColor: theme === "dark" ? "black" : "white", // Dropdown background color
//                       color: theme === "dark" ? "#fff" : "#333", // Text color
//                       borderColor: theme === "dark" ? "#444" : "#ccc", // Border color
//                       cursor: "pointer",
//                       boxShadow: "none",
//                       "&:hover": {
//                         borderColor: theme === "dark" ? "#666" : "#888", // Border color on hover
//                       },
//                     }),
//                     menu: (provided) => ({
//                       ...provided,
//                       zIndex: 999,
//                       backgroundColor: theme === "dark" ? "#000" : "#fff", // Black background for dropdown menu in dark mode
//                     }),
//                     option: (provided, state) => ({
//                       ...provided,
//                       backgroundColor: state.isFocused
//                         ? theme === "dark"
//                           ? "#666" // Gray when hovered in dark theme
//                           : "lightblue" // Light gray when hovered in light theme
//                         : theme === "dark"
//                         ? "#000"
//                         : "#fff", // Background color for options based on theme
//                       color: state.isFocused
//                         ? "#fff"
//                         : theme === "dark"
//                         ? "#fff"
//                         : "#333", // Text color
//                       cursor: "pointer",
//                     }),
//                     singleValue: (provided) => ({
//                       ...provided,
//                       color: theme === "dark" ? "#fff" : "#333", // Selected option text color
//                     }),
//                   }}
//                 />
//               </div>
//               {selectedFilter.value === "custom_date" && (
//                 <div className="input-group-datepickers">
//                   <DatePicker
//                     placeholderText="Select Date Range"
//                     className="dates"
//                     startDate={selectedStartDate}
//                     endDate={selectedEndDate}
//                     selectsRange={true}
//                     dateFormat="dd/MM/yyyy"
//                     isClearable
//                     showYearDropdown
//                     showMonthDropdown
//                     dropdownMode="select"
//                     onChange={(dates) => {
//                       const [start, end] = dates;
//                       setSelectedStartDate(start);
//                       setSelectedEndDate(end);
//                     }}
//                     renderInput={(startProps, endProps) => (
//                       <>
//                         <TextField {...startProps} variant="standard" />
//                         <TextField {...endProps} variant="standard" />
//                       </>
//                     )}
//                     startText="Start Date"
//                     endText="End Date"
//                   />
//                 </div>
//               )}

//               <button className="export-btn" onClick={handleExportButtonClick}>
//                 Export
//               </button>
//               <ExportPopup
//                 isOpen={isExportPopupOpen}
//                 onClose={() => {
//                   setIsExportPopupOpen(false);
//                   setAnchorEl(null);
//                 }}
//                 onExport={handleExport}
//                 selectedFilter={selectedFilter}
//                 selectedStartDate={selectedStartDate}
//                 selectedEndDate={selectedEndDate}
//                 anchorEl={anchorEl}
//               />

//               <Tooltip title="Refresh List" arrow>
//                 <button className="refresh-button" onClick={handleRefresh}>
//                   <TbRefresh className="refresh-icon" />
//                 </button>
//               </Tooltip>
//             </div>
//           </div>
//         </div>

//         <div className="data-list-body">
//           <div className="data-list-scroll-body-table">
//             <div className="data-list-table-report">
//               {loading ? (
//                 <div className="loader-container">
//                   <div className="loader"></div>
//                   <div>Loading...</div>
//                 </div>
//               ) : reports.length > 0 ? (
//                 <table className="table multilevel-data">
//                   <thead>
//                     <tr>
//                       <th className="center-alignment">SR. NO.</th>
//                       <th>DEPARTMENT</th>
//                       <th className="approve-status center-alignment">
//                         APPROVE PENDING
//                       </th>
//                       <th className="approved-status center-alignment">
//                         APPROVED
//                       </th>
//                       <th className="rejected-status center-alignment">
//                         REJECTED
//                       </th>
//                       <th className="returned-status center-alignment">
//                         RETURNED
//                       </th>
//                       <th className="total-statuss center-alignment">
//                         TOTAL PR REQUESTS
//                       </th>
//                     </tr>
//                   </thead>
//                   <tbody className="data-list-scroll-bodys">
//                     {reports.map((report, index) => (
//                       <tr key={index} className="hoverable-row">
//                         <td className="center-alignment">{index + 1}</td>
//                         <td>{report.department_name || "NA"}</td>
//                         <td
//                           className={`center-alignment approve-status ${
//                             report.pending === "0" ? "non-clickable" : ""
//                           }`}
//                           onClick={() =>
//                             report.pending &&
//                             report.pending > 0 &&
//                             handleViewClick(report.department_id, "pending")
//                           }
//                         >
//                           {report.pending || "0"}
//                           {report.pending !== null &&
//                             report.pending !== "0" && (
//                               <MdArrowForwardIos className="total-report-icon" />
//                             )}
//                         </td>
//                         <td
//                           className={`center-alignment approved-status ${
//                             report.approved === "0" ? "non-clickable" : ""
//                           }`}
//                           onClick={() =>
//                             report.approved &&
//                             report.approved > 0 &&
//                             handleViewClick(report.department_id, "approved")
//                           }
//                         >
//                           {report.approved || "0"}
//                           {report.approved !== null &&
//                             report.approved !== "0" && (
//                               <MdArrowForwardIos className="total-report-icon" />
//                             )}
//                         </td>
//                         <td
//                           className={`center-alignment rejected-status ${
//                             report.rejected === "0" ? "non-clickable" : ""
//                           }`}
//                           onClick={() =>
//                             report.rejected &&
//                             report.rejected > 0 &&
//                             handleViewClick(report.department_id, "rejected")
//                           }
//                         >
//                           {report.rejected || "0"}
//                           {report.rejected !== null &&
//                             report.rejected !== "0" && (
//                               <MdArrowForwardIos className="total-report-icon" />
//                             )}
//                         </td>

//                         <td
//                           className={`center-alignment returned-status ${
//                             report.returned === "0" ? "non-clickable" : ""
//                           }`}
//                           onClick={() =>
//                             report.returned &&
//                             report.returned > 0 &&
//                             handleViewClick(report.department_id, "returned")
//                           }
//                         >
//                           {report.returned || "0"}
//                           {report.returned !== null &&
//                             report.returned !== "0" && (
//                               <MdArrowForwardIos className="total-report-icon" />
//                             )}
//                         </td>

//                         <td
//                           className="total-status center-alignment"
//                           onClick={() =>
//                             handleViewClick(
//                               report.department_id,
//                               "total_schedules"
//                             )
//                           }
//                         >
//                           {report.total_pr || "0"}
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               ) : (
//                 <div className="no-users-container">
//                   <div className="no-users-message">
//                     No Schedules Report Found
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }

// export default SchedulesReport;
